import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "react-use";
import GenericTable from "@components/tables/GenericTable";
import { setSelectedAssignedFileId, setIsFileValidated, setFileStatus } from "@store/slices/validatorSlice";
import { fetchValidatorFiles } from "@store/thunks/validatorThunk";
import { toast } from "react-toastify";
import { restrictSpecialCharacters } from "@/utils/constants";
import { setFileAndLanguage } from "@/store/slices/fileLanguageSlice";
import { setFileMeta } from "@/store/slices/fileMetaSlice";

const FileDetailTableValidator = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const { assignedFiles, selectedAssignedFolderId } = useSelector((store) => store.validator);

  const [inputValue, setInputValue] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(true); // 👈 track if more pages exist
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });


  // Debounce search query
  useDebounce(
    () => {
      setDebouncedSearchQuery(inputValue.toLowerCase());
      setPageIndex(0);
    },
    300,
    [inputValue]
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };

  const fetchFiles = async () => {
    if (!selectedAssignedFolderId) return;
    setIsLoading(true);
    try {
      const res = await dispatch(
        fetchValidatorFiles(selectedAssignedFolderId, debouncedSearchQuery, pageIndex, pageSize)
      );
      if (Array.isArray(res)) {
        if (res.length < pageSize) {
          setHasNextPage(false);
        } else {
          setHasNextPage(true);
        }
      }
    } catch (err) {
      console.error("[FileDetailTableValidator] Error fetching files:", err);
      setError("Failed to fetch files. Please try again.");
      toast.error("Failed to fetch files.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAssignedFolderId) {
      fetchFiles();
    } else {
      navigate("/validator/folders");
    }
  }, [dispatch, debouncedSearchQuery, selectedAssignedFolderId, pageIndex, pageSize, navigate]);

  const handleFileClick = (fileId, row) => {
    console.log(row)
    dispatch(setSelectedAssignedFileId(fileId));
    dispatch({
      type: "validator/setSelectedFile",
      payload: row.original,
    });
    dispatch(setFileAndLanguage({
      targetLanguageName: row.original.targetName,
    }))
    dispatch(setFileMeta({
      fileName: row.original.fileName,
    }))
    dispatch(
      setIsFileValidated(row.original.vStatus === "VALIDATED" || row.original.vStatus === "SUBMITTED_TO_GS" || row.original.vStatus === "DEV_COMPLETED")
    );
    dispatch(setFileStatus(row.original.vStatus));
    navigate("/validator/folders/files/sentences", { replace: true });
  };

  const fileColumns = useMemo(
    () => [
      {
        id: "serialNo",
        header: "Sr. No.",
        cell: ({ row, table }) => {
          return pageIndex * pageSize + row.index + 1;
        },
        cellClassName: "text-center",
      },
      {
        id: "fileName",
        header: "File Name",
        accessorKey: "fileName",
        cell: ({ row }) => (
          <button
            className="text-[#ec7026] hover:text-[#d85d1a] hover:underline font-['Semantic/Strong']"
            onClick={() => handleFileClick(row.original.fileId, row)}
          >
            {row.original.fileName || "N/A"}
          </button>
        ),
      },
      {
        id: "sourceLanguage",
        header: "Source Language",
        accessorKey: "sourceName",
        cell: ({ row }) => row.original.sourceName || "N/A",
      },
      {
        id: "targetLanguage",
        header: "Target Language",
        accessorKey: "targetName",
        cell: ({ row }) => row.original.targetName || "N/A",
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "vStatus",
        cell: ({ row }) => {
          const status = row.original.vStatus || "PENDING";
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium font-['Semantic/Strong'] ${status === "APPROVED"
                ? "bg-green-100 text-green-800"
                : status === "IN_PROGRESS"
                  ? "bg-yellow-100 text-yellow-800"
                  : status === "TRANSLATED"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-gray-100 text-gray-800"
                }`}
            >
              {status}
            </span>
          );
        },
      },
      {
        id: "translatedSentences",
        header: "Translated Sentences",
        accessorKey: "totalSentences",
        cell: ({ row }) => row.original.totalSentences || 0,
      },
      {
        id: "validatedSentences",
        header: "Validated Sentences",
        accessorKey: "translatedSentenceCount",
        cell: ({ row }) => row.original.translatedSentenceCount || 0,
      },
      {
        id: "rejectedSentences",
        header: "Rejected Sentences",
        accessorKey: "rejectedSentenceCount",
        cell: ({ row }) => row.original.rejectedSentenceCount || 0,
      },
      {
        id: "percentageCompleted",
        header: "Completed (%)",
        accessorKey: "percentageCompleted",
        cell: ({ row }) => `${row.original.percentageCompleted || 0}%`,
      },
    ],
    []
  );

  const currentFiles = Array.isArray(assignedFiles[selectedAssignedFolderId])
    ? assignedFiles[selectedAssignedFolderId]
    : [];

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="lg:pr-8 lg:pt-8 flex justify-end items-center mb-6">
        <div className="relative w-full sm:w-64">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search Files..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
            value={inputValue}
            onChange={handleInputChange}
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>
      {popup.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              {popup.type === "error" ? "Error" : "Info"}
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
              {popup.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handlePopupClose}
                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="p-4 sm:p-6 lg:p-8">
        {isLoading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 rounded w-full"></div>
              <div className="h-8 bg-gray-200 rounded w-full"></div>
              <div className="h-8 bg-gray-200 rounded w-full"></div>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
              <p className="font-['Semantic/Strong']">{error}</p>
              <p className="font-['Semantic/Strong'] mt-2">Please try again later or contact support.</p>
            </div>
          </div>
        ) : currentFiles.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-lg">
              <p className="font-['Semantic/Strong']">No files found in this folder.</p>
              <p className="font-['Semantic/Strong'] mt-2">
                There are no files assigned to this folder.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="w-full overflow-x-auto">
              <GenericTable
                data={[...currentFiles].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
                columns={fileColumns}
                showPagination={true}
                manualPagination={true}
                pageIndex={pageIndex}
                pageSize={pageSize}
                hasNextPage={hasNextPage}
                onPaginationChange={({ pageIndex, pageSize }) => {
                  setPageIndex(pageIndex);
                  setPageSize(pageSize);
                }}
                emptyMessage="No files available in this folder"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileDetailTableValidator;